import { Navigate, useLocation } from 'react-router-dom';
import parentPortalComponents from '../../../../../config/semantic-components/parent-portal-intellio-v2.json';
import parentPortalRoutesConfig from '../../../../../config/semantic-routes/parent-portal.json';
import {
  SemanticRouteAdapter,
  type SemanticComponentsConfig,
  type SemanticInternalRenderInput,
  type SemanticRoutesConfig,
} from '../SemanticRouteAdapter';
import { ParentPortalPage, type ParentPortalPageMode } from '../../parent-portal/pages/ParentPortalPage';

const componentsConfig = parentPortalComponents as SemanticComponentsConfig;
const routesConfig = parentPortalRoutesConfig as SemanticRoutesConfig;

const COMPONENT_TO_CANONICAL_PATH: Record<string, string> = {
  'portal-family-dashboard': '/parent/portal/dashboard',
  'portal-family-shell': '/parent/portal/applications',
  'portal-family-applications': '/parent/portal/applications',
  'portal-family-enrollment': '/parent/portal/enrollment',
  'portal-family-financial-aid': '/parent/portal/financialaid',
  'portal-family-settings': '/parent/portal/settings',
  'portal-family-calendar': '/parent/portal/calendar',
  'portal-family-tuition': '/parent/portal/tuition',
  'portal-family-attendance': '/parent/portal/attendance',
  'portal-family-academic': '/parent/portal/academic',
  'portal-family-resources': '/parent/portal/resources',
  'portal-new-family-applications': '/parent/portal--new/applications',
  'portal-new-family-enrollment': '/parent/portal--new/enrollment',
  'portal-new-family-financial-aid': '/parent/portal--new/financialaid',
  'portal-new-family-settings': '/parent/portal--new/settings',
};
const DEFAULT_PARENT_PORTAL_PATH = '/parent/portal/dashboard';

type ParentPortalComponentId = keyof typeof COMPONENT_TO_CANONICAL_PATH;
type ParentPortalSurfaceRenderer = ParentPortalPageMode;

const COMPONENT_RENDERER: Record<ParentPortalComponentId, ParentPortalSurfaceRenderer> = {
  'portal-family-dashboard': 'dashboard',
  'portal-family-shell': 'applications',
  'portal-family-applications': 'applications',
  'portal-family-enrollment': 'enrollment',
  'portal-family-financial-aid': 'financial-aid',
  'portal-family-settings': 'settings',
  'portal-family-calendar': 'calendar',
  'portal-family-tuition': 'tuition',
  'portal-family-attendance': 'attendance',
  'portal-family-academic': 'academic',
  'portal-family-resources': 'resources',
  'portal-new-family-applications': 'intake-applications',
  'portal-new-family-enrollment': 'intake-enrollment',
  'portal-new-family-financial-aid': 'intake-financial-aid',
  'portal-new-family-settings': 'intake-settings',
};

const mapCanonicalParentPortalToSemanticPath = (pathname: string | null): string | null => {
  if (!pathname) return pathname;
  if (pathname === '/parent/portal') {
    return '/portal/family/applications';
  }
  if (pathname === '/parent/portal--new') {
    return '/portal/family/intake/applications';
  }

  if (pathname.startsWith('/parent/portal--new/')) {
    const suffix = pathname.slice('/parent/portal--new/'.length);
    const [head = 'applications', ...rest] = suffix.split('/');
    const normalizedHead = head === 'financialaid' ? 'financial-aid' : head;
    return `/portal/family/intake/${normalizedHead}${rest.length ? `/${rest.join('/')}` : ''}`;
  }

  if (pathname.startsWith('/parent/portal/')) {
    const suffix = pathname.slice('/parent/portal/'.length);
    if (suffix.startsWith('dashboard')) {
      return `/portal/family/dashboard${suffix.slice('dashboard'.length)}`;
    }
    if (suffix.startsWith('applications')) {
      return `/portal/family/applications${suffix.slice('applications'.length)}`;
    }
    if (suffix.startsWith('financialaid')) {
      return `/portal/family/financial-aid${suffix.slice('financialaid'.length)}`;
    }
    if (suffix.startsWith('financial-aid')) {
      return `/portal/family/financial-aid${suffix.slice('financial-aid'.length)}`;
    }
    return `/portal/family/${suffix}`;
  }

  return pathname;
};

const isParentPortalComponentId = (value: string): value is ParentPortalComponentId =>
  Object.prototype.hasOwnProperty.call(COMPONENT_RENDERER, value);

const ParentPortalSurface = ({
  componentId,
  internalPath,
  pathname,
}: SemanticInternalRenderInput) => {
  const location = useLocation();
  if (pathname !== internalPath) {
    return <Navigate to={`${internalPath}${location.search}${location.hash}`} replace />;
  }

  if (!isParentPortalComponentId(componentId)) {
    return <Navigate to={DEFAULT_PARENT_PORTAL_PATH} replace />;
  }

  const renderer = COMPONENT_RENDERER[componentId];
  return <ParentPortalPage mode={renderer} />;
};

export function ParentPortalSemanticRoute() {
  return (
    <section data-extracted-from="owned:ystnj/semantic/parent-portal-route">
      <SemanticRouteAdapter
        moduleKey="parent-portal"
        componentsConfig={componentsConfig}
        routesConfig={routesConfig}
        mapIntegrationPath={mapCanonicalParentPortalToSemanticPath}
        strictNoFallback
        blockBridgedTarget
        internal={{
          resolvePath: (componentId: string) => COMPONENT_TO_CANONICAL_PATH[componentId] ?? null,
          render: (input) => <ParentPortalSurface {...input} />,
        }}
      />
    </section>
  );
}
